
import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend
} from "recharts";
import { Card, Row, Col, ButtonGroup, Button } from "react-bootstrap";
import { getSTATS } from "../../services/Admin/adminServices";
import toast from "react-hot-toast";
import ListingDistribution from "../Modals/PietChart";
import { useSelector } from "react-redux";



const MetricCard = ({ title, value, icon, description, mdiscription, growth }) => {
  const [displayValue, setDisplayValue] = useState(
    typeof value === "number" ? 0 : value
  );

  useEffect(() => {
    const stringValue = value?.toString() ?? "";

    // Handle ratio values (e.g. "5 / 10")
    const ratioMatch = stringValue.match(/^([\d,.]+)\s*\/\s*([\d,.]+)$/);

    if (ratioMatch) {
      const targetA = parseFloat(ratioMatch[1].replace(/,/g, ""));
      const targetB = parseFloat(ratioMatch[2].replace(/,/g, ""));

      if (isNaN(targetA) || isNaN(targetB)) {
        setDisplayValue(stringValue);
        return;
      }

      let startTimestamp = null;
      const duration = 1500;

      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;

        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easeOut =
          progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

        const currentA = Math.floor(easeOut * targetA);
        const currentB = Math.floor(easeOut * targetB);

        setDisplayValue(
          `${currentA.toLocaleString("en-IN")} / ${currentB.toLocaleString(
            "en-IN"
          )}`
        );

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          setDisplayValue(
            `${targetA.toLocaleString("en-IN")} / ${targetB.toLocaleString(
              "en-IN"
            )}`
          );
        }
      };

      requestAnimationFrame(step);
      return;
    }

    // Handle percentage values (e.g. "75%" or "75.5%")
    const percentageMatch = stringValue.match(/^([\d,.]+)\s*%$/);

    if (percentageMatch) {
      const target = parseFloat(percentageMatch[1].replace(/,/g, ""));

      if (isNaN(target)) {
        setDisplayValue(stringValue);
        return;
      }

      let startTimestamp = null;
      const duration = 1500;

      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;

        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const easeOut =
          progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

        const current = easeOut * target;

        setDisplayValue(
          `${Number.isInteger(target) ? Math.floor(current) : current.toFixed(1)}%`
        );

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          setDisplayValue(`${target}%`);
        }
      };

      requestAnimationFrame(step);
      return;
    }

    // Handle single numeric value
    const target =
      typeof value === "number"
        ? value
        : parseFloat(stringValue.replace(/,/g, ""));

    if (isNaN(target)) {
      setDisplayValue(value);
      return;
    }

    let startTimestamp = null;
    const duration = 1500;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;

      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOut =
        progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

      setDisplayValue(
        Math.floor(easeOut * target).toLocaleString("en-IN")
      );

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setDisplayValue(target.toLocaleString("en-IN"));
      }
    };

    requestAnimationFrame(step);
  }, [value]);

  return (
    <Card
      className="p-3 mb-3 shadow-sm border-0"
      style={{ backgroundColor: "#fff", borderRadius: "1.36rem" }}
    >
      <div
        className="d-flex align-items-start"
        style={{ paddingTop: "1.99rem", paddingBottom: "1.99rem" }}
      >
        <div
          className="d-flex justify-content-center align-items-center rounded-circle me-3"
          style={{ width: "48px", height: "48px", backgroundColor: "#FFE4E6", color: "#E5484D" }}
        >
          {icon}
        </div>

        <div>
          <h6
            className="mb-1"
            style={{ fontSize: "0.99rem", color: "black", fontWeight: "normal" }}
          >
            {title}{" "}
            {growth && (<span
              style={{
                color: growth > 0 ? "#16a34a" : growth < 0 ? "#dc2626" : "#6b7280",
                fontWeight: 600,
              }}
            >
              {growth > 0 ? `: +${growth}` : `: ${growth}`}
            </span>)}

          </h6>
          {description ? (
            <p className="mb-2" style={{ fontSize: "0.9rem" }}>
              {description}
            </p>
          ) : (
            ""
          )}
          {mdiscription ? (
            <p className="mb-2" style={{ fontSize: "0.9rem" }}>
              {mdiscription}
            </p>
          ) : (
            ""
          )}
          <h4 className="mb-2" style={{ fontSize: "1.45rem" }}>
            {displayValue}
          </h4>
        </div>
      </div>
    </Card>
  );
};



const ChartHeader = ({ activeTab, setActiveTab, title }) => {

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };
  return (
    <div className="d-flex justify-content-between align-items-center mb-4">
      <h5 className=" mb-0">{title}</h5>
      <ButtonGroup size="sm" className="border rounded overflow-hidden">
        <Button
          variant={activeTab === "Monthly" ? "dark" : "light"}
          onClick={() => setActiveTab("Monthly")}
          className="border-0 px-3"
        >
          Monthly
        </Button>

        <Button
          variant={activeTab === "Weekly" ? "dark" : "light"}
          onClick={() => setActiveTab("Weekly")}
          className="border-0 px-3"
        >
          Weekly
        </Button>

        <Button
          variant={activeTab === "Today" ? "dark" : "light"}
          onClick={() => setActiveTab("Today")}
          className="border-0 px-3"
        >
          Today
        </Button>
      </ButtonGroup>
    </div>
  )
}



const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Monthly");
  const [actveTabListing, setActiveTabListing] = useState("Monthly");
  const [data, setData] = useState([])
  const [userChartData, setUserChartData] = useState([])
  const [listingChartData, setListingChartData] = useState([])
  const [activeFlatChartData, setActiveFlatChartData] = useState([])
  const [activeFlatMateChartData, setActiveFlatMateChartData] = useState([])
  const [flatDate, setFlatDate] = useState("Monthly")
  const [flatMateDate, setFlatMateDate] = useState("Monthly")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [location, setLocation] = useState([])
  const fetchData = async () => {
    try {
      const response = await getSTATS(startDate, endDate)
      const data = response?.data?.data

      const updatedLocationDistribution = data?.graphs?.locationDistribution?.map((item, index) => ({
        ...item,
        color: ["#E5484D", "#34D399", "#3B82F6", "#F59E0B"][index % 4],
        value: item.percentage,
      }));
      setLocation(updatedLocationDistribution);


      setData(data)

    } catch (error) {
      // toast.error(error?.response?.data?.message || "Failed to load data");
      console.log(error?.response?.data?.message || "Failed to load data");
    }
  }

  useEffect(() => {
    fetchData()
  }, [endDate])
  const fetchUserData = async () => {
    try {
      let startDates;
      let endDates
      if (activeTab === "Monthly") {
        startDates = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (activeTab === "Weekly") {
        startDates = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (activeTab === "Today") {
        startDates = new Date().toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      }

      const response = await getSTATS(startDates, endDates)
      console.log("response ", response)
      const data = response?.data?.data
      const users = response?.data?.data?.graphs?.userGrowthTrends || [];




      setUserChartData(users);
    } catch (error) {

      console.log(error?.response?.data?.message || "Failed to load data");
    }
  }

  useEffect(() => {
    fetchUserData()
  }, [activeTab])

  const fetchListingData = async () => {
    try {
      let startDates;
      let endDates
      if (actveTabListing === "Monthly") {
        startDates = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (actveTabListing === "Weekly") {
        startDates = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (actveTabListing === "Today") {
        startDates = new Date().toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      }

      const response = await getSTATS(startDates, endDates)

      const data = response?.data?.data
      const users = response?.data?.data?.graphs?.listingPostedTrends || [];

      setListingChartData(users);
    } catch (error) {
      // toast.error(error?.response?.data?.message || "Failed to load data");
      console.log(error?.response?.data?.message || "Failed to load data");
    }
  }
  useEffect(() => {
    fetchListingData()
  }, [actveTabListing])

  const rolesk = useSelector((state) => state.role.roles);

  const fetchFlatData = async () => {
    try {
      let startDates;
      let endDates
      if (flatDate === "Monthly") {
        startDates = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (flatDate === "Weekly") {
        startDates = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (flatDate === "Today") {
        startDates = new Date().toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      }

      const response = await getSTATS(startDates, endDates)

      const data = response?.data?.data
      const users = response?.data?.data?.graphs?.flatActiveVsExpireTrends || [];

      setActiveFlatChartData(users);
    } catch (error) {
      // toast.error(error?.response?.data?.message || "Failed to load data");
      console.log(error?.response?.data?.message || "Failed to load data");
    }
  }
  useEffect(() => {
    fetchFlatData()
  }, [flatDate])

  const fetchFlatMateData = async () => {
    try {
      let startDates;
      let endDates
      if (flatMateDate === "Monthly") {
        startDates = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (flatMateDate === "Weekly") {
        startDates = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      } else if (flatMateDate === "Today") {
        startDates = new Date().toISOString().split('T')[0]
        endDates = new Date().toISOString().split('T')[0]
      }

      const response = await getSTATS(startDates, endDates)

      const data = response?.data?.data
      const users = response?.data?.data?.graphs?.flatmateActiveVsExpireTrends || [];

      setActiveFlatMateChartData(users);
    } catch (error) {
      // toast.error(error?.response?.data?.message || "Failed to load data");
      console.log(error?.response?.data?.message || "Failed to load data");
    }
  }

  useEffect(() => {
    fetchFlatMateData()
  }, [flatMateDate])




  const topMetrics = [
    {
      title: "Total Registered Users",
      value: data?.users?.verified,
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.3106 7.15719C20.7902 8.08488 21.8277 9.65473 22.0166 11.477C22.62 11.7589 23.2898 11.921 23.9998 11.921C26.592 11.921 28.693 9.82006 28.693 7.22827C28.693 4.63608 26.592 2.5351 23.9998 2.5351C21.4324 2.53589 19.3498 4.59934 19.3106 7.15719ZM16.236 16.7664C18.8282 16.7664 20.9292 14.665 20.9292 12.0732C20.9292 9.48141 18.8278 7.38042 16.236 7.38042C13.6442 7.38042 11.542 9.48181 11.542 12.0736C11.542 14.6654 13.6442 16.7664 16.236 16.7664ZM18.2268 17.0863H14.2445C10.931 17.0863 8.23542 19.7823 8.23542 23.0957V27.9658L8.2478 28.042L8.58325 28.1471C11.7453 29.1351 14.4924 29.4645 16.7536 29.4645C21.17 29.4645 23.7298 28.2054 23.8876 28.1251L24.2011 27.9666H24.2346V23.0957C24.2358 19.7823 21.5402 17.0863 18.2268 17.0863ZM25.9914 12.2413H22.0398C21.9971 13.8224 21.3222 15.246 20.2547 16.27C23.1999 17.1458 25.3548 19.8769 25.3548 23.1029V24.6036C29.2565 24.4607 31.5048 23.3549 31.653 23.2806L31.9665 23.1216H32V18.25C32 14.9369 29.3044 12.2413 25.9914 12.2413ZM8.001 11.9218C8.91911 11.9218 9.77332 11.6539 10.4969 11.1974C10.727 9.69706 11.5313 8.38599 12.6802 7.49304C12.685 7.40518 12.6934 7.31812 12.6934 7.22947C12.6934 4.63728 10.592 2.53629 8.001 2.53629C5.40841 2.53629 3.30782 4.63728 3.30782 7.22947C3.30782 9.82046 5.40841 11.9218 8.001 11.9218ZM12.2157 16.27C11.1535 15.2512 10.481 13.8351 10.4318 12.2637C10.2853 12.2529 10.1403 12.2413 9.99096 12.2413H6.00904C2.69562 12.2413 0 14.9369 0 18.25V23.1208L0.0123799 23.1959L0.347835 23.3018C2.88451 24.0937 5.14843 24.4587 7.11484 24.5737V23.1029C7.11564 19.8769 9.26974 17.1466 12.2157 16.27Z" fill="#E5484D" />
        </svg>

      ),
    },
    {
      title: "Incomplete Profiles",
      description: "Users who have verified their OTP",
      value: data?.users?.inCompleteUsers,
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 2C11.9291 2 8.61719 5.31193 8.61719 9.38281C8.61719 13.4537 11.9291 16.7656 16 16.7656C20.0709 16.7656 23.3828 13.4537 23.3828 9.38281C23.3828 5.31193 20.0709 2 16 2Z" fill="#E5484D" />
          <path d="M25.1856 21.5888C23.1644 19.5365 20.4849 18.4062 17.6406 18.4062H14.3594C11.5152 18.4062 8.83561 19.5365 6.81436 21.5888C4.80301 23.631 3.69531 26.3268 3.69531 29.1797C3.69531 29.6327 4.06259 30 4.51562 30H27.4844C27.9374 30 28.3047 29.6327 28.3047 29.1797C28.3047 26.3268 27.197 23.631 25.1856 21.5888Z" fill="#E5484D" />
        </svg>

      ),
    },
    {
      title: "Unverified Users",

      description: "Users who have not completed face verification.",
      value: data?.users?.unVerifiedUsers,
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 2C11.9291 2 8.61719 5.31193 8.61719 9.38281C8.61719 13.4537 11.9291 16.7656 16 16.7656C20.0709 16.7656 23.3828 13.4537 23.3828 9.38281C23.3828 5.31193 20.0709 2 16 2Z" fill="#E5484D" />
          <path d="M25.1856 21.5888C23.1644 19.5365 20.4849 18.4062 17.6406 18.4062H14.3594C11.5152 18.4062 8.83561 19.5365 6.81436 21.5888C4.80301 23.631 3.69531 26.3268 3.69531 29.1797C3.69531 29.6327 4.06259 30 4.51562 30H27.4844C27.9374 30 28.3047 29.6327 28.3047 29.1797C28.3047 26.3268 27.197 23.631 25.1856 21.5888Z" fill="#E5484D" />
        </svg>

      ),
    },

    {
      title: "Property Acceptance",
      value: `${data?.ads?.acceptedBookingInterest ?? 0} / ${data?.ads?.totalBookingInterest ?? 0}`,
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21.5866 2.66669H10.4133C5.55996 2.66669 2.66663 5.56002 2.66663 10.4134V21.5867C2.66663 26.44 5.55996 29.3334 10.4133 29.3334H21.5866C26.44 29.3334 29.3333 26.44 29.3333 21.5867V10.4134C29.3333 5.56002 26.44 2.66669 21.5866 2.66669ZM13.2933 19.8667L10.2933 22.8667C10.0933 23.0667 9.83996 23.16 9.58663 23.16C9.33329 23.16 9.06663 23.0667 8.87996 22.8667L7.87996 21.8667C7.47996 21.48 7.47996 20.84 7.87996 20.4534C8.26663 20.0667 8.89329 20.0667 9.29329 20.4534L9.58663 20.7467L11.88 18.4534C12.2666 18.0667 12.8933 18.0667 13.2933 18.4534C13.68 18.84 13.68 19.48 13.2933 19.8667ZM13.2933 10.5334L10.2933 13.5334C10.0933 13.7334 9.83996 13.8267 9.58663 13.8267C9.33329 13.8267 9.06663 13.7334 8.87996 13.5334L7.87996 12.5334C7.47996 12.1467 7.47996 11.5067 7.87996 11.12C8.26663 10.7334 8.89329 10.7334 9.29329 11.12L9.58663 11.4134L11.88 9.12002C12.2666 8.73335 12.8933 8.73335 13.2933 9.12002C13.68 9.50669 13.68 10.1467 13.2933 10.5334ZM23.4133 22.16H16.4133C15.8666 22.16 15.4133 21.7067 15.4133 21.16C15.4133 20.6134 15.8666 20.16 16.4133 20.16H23.4133C23.9733 20.16 24.4133 20.6134 24.4133 21.16C24.4133 21.7067 23.9733 22.16 23.4133 22.16ZM23.4133 12.8267H16.4133C15.8666 12.8267 15.4133 12.3734 15.4133 11.8267C15.4133 11.28 15.8666 10.8267 16.4133 10.8267H23.4133C23.9733 10.8267 24.4133 11.28 24.4133 11.8267C24.4133 12.3734 23.9733 12.8267 23.4133 12.8267Z" fill="#E5484D" />
        </svg>

      ),
    },
    {
      title: "Total Ads",
      value: data?.ads?.total,
      icon: (<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_1516_1380)">
          <path d="M2.14899 20.2117H5.05441C5.51286 20.2117 5.88455 20.5833 5.88455 21.0419V30.5882C5.88455 31.0467 5.51292 31.4184 5.05441 31.4184H2.14899C1.69054 31.4184 1.31885 31.0467 1.31885 30.5882V21.0419C1.31891 20.5834 1.69054 20.2117 2.14899 20.2117Z" fill="#E5484D" />
          <path d="M24.5621 14.4009H27.4675C27.9259 14.4009 28.2976 14.7726 28.2976 15.2311V30.5882C28.2976 31.0467 27.926 31.4184 27.4675 31.4184H24.5621C24.1036 31.4184 23.7319 31.0467 23.7319 30.5882V15.2311C23.732 14.7726 24.1036 14.4009 24.5621 14.4009Z" fill="#E5484D" />
          <path d="M9.62006 16.4762H12.5255C12.9839 16.4762 13.3556 16.8479 13.3556 17.3064V30.5882C13.3556 31.0467 12.984 31.4184 12.5255 31.4184H9.62006C9.16161 31.4184 8.78992 31.0467 8.78992 30.5882V17.3064C8.78992 16.8479 9.16161 16.4762 9.62006 16.4762Z" fill="#E5484D" />
          <path d="M17.0911 21.872H19.9966C20.455 21.872 20.8267 22.2436 20.8267 22.7021V30.5882C20.8267 31.0467 20.4551 31.4184 19.9966 31.4184H17.0911C16.6327 31.4184 16.261 31.0467 16.261 30.5882V22.7021C16.261 22.2436 16.6326 21.872 17.0911 21.872Z" fill="#E5484D" />
          <path d="M24.3803 3.23754L24.6808 3.53761C25.005 3.86174 25.0052 4.38731 24.681 4.71157C24.681 4.71163 24.6809 4.71175 24.6808 4.71182L18.5844 10.8094C18.2603 11.1335 17.7348 11.1335 17.4107 10.8094L10.6789 4.07762C10.3547 3.75336 9.82916 3.75324 9.5049 4.07737C9.50484 4.07743 9.50471 4.07755 9.50465 4.07762L0.243051 13.3397C-0.081017 13.6638 -0.081017 14.1893 0.243051 14.5134L2.23115 16.502C2.55528 16.8262 3.08085 16.8263 3.40511 16.5022L3.40536 16.502L9.50465 10.4023C9.82878 10.078 10.3544 10.0779 10.6786 10.402L10.6789 10.4023L17.4107 17.1341C17.7348 17.4582 18.2603 17.4582 18.5844 17.1341L27.8432 7.87581C28.1673 7.55174 28.6928 7.55174 29.0169 7.87581L29.3423 8.20119C29.6664 8.52551 30.192 8.52569 30.5163 8.20163C30.6323 8.08569 30.7114 7.93787 30.7435 7.77699L31.9837 1.57439C32.0735 1.12482 31.7819 0.687558 31.3323 0.597743C31.225 0.576306 31.1144 0.576306 31.0071 0.597743L24.8045 1.83833C24.3551 1.92908 24.0643 2.36696 24.1551 2.81634C24.1873 2.97579 24.2656 3.12223 24.3803 3.23754Z" fill="#E5484D" />
        </g>
        <defs>
          <clipPath id="clip0_1516_1380">
            <rect width="32" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>),
    },
    {
      title: "Active Ads",
      value: data?.ads?.active,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" />
        </svg>
      ),
    },
    {
      title: "Expiry Ads",
      value: data?.ads?.expireAds,
      icon: (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="3"
            y="5"
            width="18"
            height="16"
            rx="2"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M8 3V7M16 3V7M3 9H21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M9 13L15 19M15 13L9 19"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: `Ad Type (Total Flat): ${data?.ads?.flat}`,

      description: `Active Flat  : ${data?.ads?.activeAdsFlat}`,
      mdescription: `Expired Flat  : ${data?.ads?.expireAdsFlat}`,

      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 10l8-6 8 6v10H4V10zm6 8h4v-5h-4v5z" />
        </svg>
      ),
    },
    {
      title: `Ad Type (Total Flatmate): ${data?.ads?.flatmate}`,
      description: `Active Flatmate  : ${data?.ads?.activeAdsFlatmate}`,
      mdescription: `Expired Flatmate  : ${data?.ads?.expireAdsFlatmate}`,

      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 11c1.66 0 3-1.34 3-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.96 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
        </svg>
      ),
    },
    {
      title: `User Growth Rate`,
      growth: `${data?.users?.currentMonthUsers - data?.users?.lastMonthUsers > 0 ? `${data?.users?.currentMonthUsers - data?.users?.lastMonthUsers}` : data?.users?.currentMonthUsers - data?.users?.lastMonthUsers}`,
      description: `This Month : ${data?.users?.currentMonthUsers}`,
      mdescription: `Last Month : ${data?.users?.lastMonthUsers}`,

      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 6l2.29 2.29-5.88 5.88-4-4L2 16.59 3.41 18l5-5 4 4 7.29-7.29L22 12V6z" />
        </svg>
      ),
    },
    {
      title: "Total Reported Users",
      value: `${data?.users?.totalReports}`,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 14h-2V9h2v5zm0 4h-2v-2h2v2zm-1-16L1 21h22L12 2z" />
        </svg>
      ),
    },
    {
      title: "Total Blocked Users",
      value: `${data?.users?.totalBlockedUsers}`,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6 10c0 1.61-.59 3.09-1.57 4.23L7.77 7.57A7.94 7.94 0 0112 6c3.31 0 6 2.69 6 6zm-12 0c0-1.61.59-3.09 1.57-4.23l8.66 8.66A7.94 7.94 0 0112 18c-3.31 0-6-2.69-6-6z" />
        </svg>
      ),
    },
  ];

  const saftyCheckData = [
    {
      title: "Total Safety Checks",
      value: data?.safetyCheck?.total,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L4 5V11C4 16 7.4 20.7 12 22C16.6 20.7 20 16 20 11V5L12 2Z"
            fill="#0D6EFD" />
        </svg>
      ),
    },
    {
      title: "Pending Safety Checks",
      value: data?.safetyCheck?.pendingSafetyChecks,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#FFC107" />
          <path d="M12 7V12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>

      ),
    },
    {
      title: "Active Safety Checks",
      value: data?.safetyCheck?.activeSafetyChecks,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#198754" />
          <path d="M10 8L16 12L10 16V8Z" fill="white" />
        </svg>

      ),
    },
    {
      title: "Clocked Out Safety Checks",
      value: data?.safetyCheck?.clockedOutSafetyChecks,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="10" height="16" rx="2" fill="#6C757D" />
          <path d="M14 12H21M18 9L21 12L18 15" stroke="white" strokeWidth="2" />
        </svg>

      ),
    },
    {
      title: "Overdue Safety Checks",
      value: data?.safetyCheck?.overdueSafetyChecks,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L22 20H2L12 2Z" fill="#DC3545" />
          <path d="M12 8V13" stroke="white" strokeWidth="2" />
          <circle cx="12" cy="17" r="1.5" fill="white" />
        </svg>

      ),
    },
    {
      title: "Cancelled Safety Checks",
      value: data?.safetyCheck?.cancelledSafetyChecks,
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#6C757D" />
          <path d="M8 8L16 16M16 8L8 16"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round" />
        </svg>

      ),
    },
  ]




  const renderScrollableLegend = (props) => {
    const { payload, onClick } = props;

    return (
      <div
        style={{
          maxHeight: '220px',
          overflowY: 'auto',
          paddingRight: '5px'
        }}
        className="custom-scrollbar" // Optional: Use this class if you want to style the scrollbar in CSS
      >
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {payload.map((entry, index) => (
            <li key={`item-${index}`} className="d-flex align-items-center mb-2" onClick={() => onClick && onClick(entry)}
              style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>
              <span
                style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  backgroundColor: entry.color,
                  borderRadius: '50%',
                  marginRight: '8px',
                  flexShrink: 0
                }}
              ></span>
              <span style={{ color: '#4B5563', fontSize: '14px', whiteSpace: 'nowrap' }}>
                {entry.value} <span className="ms-2 fw-bold text-dark">{entry.payload.value}%</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };


  const renderLegend = (props) => {
    const { payload } = props;

    // force exact order
    const order = ["Registered User", "Active User"];

    const sorted = [...payload].sort(
      (a, b) => order.indexOf(a.value) - order.indexOf(b.value)
    );



    return (
      <div className="d-flex justify-content-center gap-4 mt-2">
        {sorted.map((entry, index) => (
          <div key={index} className="d-flex align-items-center gap-2">


            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",

                backgroundColor: entry.color,
              }}
            />

            <span style={{ fontSize: "14px", color: "#6B7280" }}>
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };
  const today = new Date().toISOString().split("T")[0];
  return (
    <div className="container-fluid mt-4" style={{ backgroundColor: "#F8F9FA", minHeight: "100vh", paddingBottom: "2rem" }}>
      <h2 className="mb-4 fw-bold" style={{ color: "#E5484D" }}>
        Analytics & Metrics
      </h2>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-center mb-4">
        <div className="d-flex gap-4 align-items-center" >
          <div>
            <label className="form-label mb-1" style={{ fontSize: "12px" }}>
              Start Date
            </label>
            <input
              type="date"
              style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
              className="form-control"
              max={today}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                if (endDate && e.target.value > endDate) {
                  setEndDate("");
                }
              }

              }
            />
          </div>

          <div>
            <label className="form-label mb-1" style={{ fontSize: "12px" }}>
              End Date
            </label>
            <input
              type="date"
              style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
              className="form-control"
              value={endDate}
              min={startDate}

              onChange={(e) => setEndDate(e.target.value)}
              disabled={!startDate}
            />
          </div>


          {endDate && (
            <div className="d-flex flex-column">
              <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                &nbsp;
              </label>
              <button
                onClick={() => {
                  setStartDate("")
                  setEndDate("")
                }}
                style={{
                  backgroundColor: "#E5484D",
                  color: "#fff",
                  border: "none",
                  borderRadius: "0.5rem",
                  padding: "0.5rem 1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(229, 72, 77, 0.25)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#d63f45";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#E5484D";
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>

      </div>

      <Row>
        {topMetrics.map((metric, idx) => (
          <Col md={4} key={idx}>
            <MetricCard title={metric.title} value={metric.value} icon={metric.icon} description={metric.description} mdiscription={metric.mdescription} growth={metric.growth} />
          </Col>
        ))}
      </Row>

      <h2 style={{ color: "#E5484D" }} className="fw-bold"> Safety Check Metrics</h2>
      <Row>
        {saftyCheckData.map((metric, idx) => (
          <Col md={4} key={idx}>
            <MetricCard title={metric.title} value={metric.value} icon={metric.icon} />
          </Col>
        ))}
      </Row>






      {/* <Row>
        <Col md={12} className="mb-4">
    <Card className="p-4 shadow-sm border-0 rounded-4 h-100">
      <h5 style={{fontSize:"1.2rem"}} className="mb-4">Listing Distribution by Location</h5>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={location}
            cx="35%"           
            cy="50%"
            innerRadius={60}
            outerRadius={95}
            paddingAngle={2}
            dataKey="value"
          >
            {location.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
          <Legend 
            content={renderScrollableLegend} 
            layout="vertical" 
            verticalAlign="middle" 
            align="right"
            wrapperStyle={{ width: '55%', right: 0 }}  
          />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  </Col>
</Row> */}

      <ListingDistribution location={location} renderScrollableLegend={renderScrollableLegend} />

      <Card className="p-4 mb-4 shadow-sm border-0 rounded-4">
        <ChartHeader activeTab={activeTab} setActiveTab={setActiveTab} title="User Growth Trends" />
        {Array.isArray(userChartData) && userChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={userChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E5484D" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#E5484D" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorAct" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34D399" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#34D399" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
              <Tooltip />
              <Legend content={renderLegend} />
              <Area type="monotone" dataKey="active" name="Verified User" stroke="#34D399" strokeWidth={3} fillOpacity={1} fill="url(#colorAct)" />
              <Area type="monotone" dataKey="registered" name="Unverified User" stroke="#E5484D" strokeWidth={3} fillOpacity={1} fill="url(#colorReg)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="d-flex align-items-center justify-content-center" style={{ height: "300px", color: "#6B7280", fontWeight: "500" }}>
            No data available for the selected filter
          </div>
        )}
      </Card>

      {/* --- Bottom Two Charts --- */}
      <Row>
        <Col md={12} className="mb-4">
          <Card className="p-4 shadow-sm border-0 rounded-4 h-100">
            <ChartHeader style={{ fontSize: "1.34rem" }} activeTab={actveTabListing} setActiveTab={setActiveTabListing} title="Listing Posted" />
            {Array.isArray(listingChartData) && listingChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={listingChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFlat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E5484D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#E5484D" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34D399" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#34D399" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} iconType="square" />
                  <Area type="monotone" dataKey="findFlat" name=" Flat" stroke="#E5484D" strokeWidth={3} fillOpacity={1} fill="url(#colorFlat)" />
                  <Area type="monotone" dataKey="findFlatmate" name=" Flatmate" stroke="#34D399" strokeWidth={3} fillOpacity={1} fill="url(#colorMate)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: "250px", color: "#6B7280", fontWeight: "500" }}>
                No data available for the selected filter
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-4">
          <h1 className="fw-bold" style={{ color: "#E5484D" }}> Flats</h1>
          <Card className="p-4 shadow-sm border-0 rounded-4 h-100">
            <ChartHeader style={{ fontSize: "1.34rem" }} activeTab={flatDate} setActiveTab={setFlatDate} title="Active  Vs Expired    " />
            {Array.isArray(activeFlatChartData) && activeFlatChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={activeFlatChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFlat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E5484D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#E5484D" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34D399" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#34D399" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} iconType="square" />
                  <Area type="monotone" dataKey="expired" name="  Expired" stroke="#E5484D" strokeWidth={3} fillOpacity={1} fill="url(#colorFlat)" />
                  <Area type="monotone" dataKey="active" name="Active " stroke="#34D399" strokeWidth={3} fillOpacity={1} fill="url(#colorMate)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: "250px", color: "#6B7280", fontWeight: "500" }}>
                No data available for the selected filter
              </div>
            )}
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <h1 style={{ color: "#E5484D" }} className="fw-bold"> Flatmates</h1>
          <Card className="p-4 shadow-sm border-0 rounded-4 h-100">
            <ChartHeader style={{ fontSize: "1.34rem" }} activeTab={flatMateDate} setActiveTab={setFlatMateDate} title="Active   Vs Expire    " />
            {Array.isArray(activeFlatMateChartData) && activeFlatMateChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={activeFlatMateChartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFlat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E5484D" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#E5484D" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34D399" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#34D399" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} iconType="square" />
                  <Area type="monotone" dataKey="expired" name="  Expired" stroke="#E5484D" strokeWidth={3} fillOpacity={1} fill="url(#colorFlat)" />
                  <Area type="monotone" dataKey="active" name="Active" stroke="#34D399" strokeWidth={3} fillOpacity={1} fill="url(#colorMate)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: "250px", color: "#6B7280", fontWeight: "500" }}>
                No data available for the selected filter
              </div>
            )}
          </Card>
        </Col>
      </Row>




    </div>
  );
};

export default AnalyticsDashboard;